/**
 * Amazon Connect contacts hook — thin bridge between ContactSessionManager
 * (pure state machine) and React/Redux (side effects + state).
 *
 * Replaces the previous ~700-line implementation that used 6 refs and
 * scattered guard clauses.  All contact lifecycle logic now lives in
 * src/connect/contactSession.ts + src/connect/contactMachine.ts.
 */

import {
  createElement,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { useDispatch } from 'react-redux';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { callService } from '@/services/callService';
import { useAmazonConnectReady } from '@/components/amazon-connect/AmazonConnectCCP';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import {
  isContactInMissedState,
  isContactInAfterChatWorkState,
  hasActiveCallLeg,
} from '@/components/amazon-connect/contactUtils';
import { FORCED_RELEASE_STATE } from '@/constants/amazonConnect';

import type { ConnectContact } from '@/types';
import {
  registerContactSession,
  setContactInformation,
} from '@/store/slices/callSlice';
import { fetchCallMetadata } from '@/store/api/call';
import type { ContactEffects } from '@/connect/effects';
import type { SessionHelpers } from '@/connect/contactSession';
import { ContactSessionManager } from '@/connect/contactSession';

// ---------------------------------------------------------------------------
// Public API (unchanged)
// ---------------------------------------------------------------------------

export interface AmazonConnectContactsContextValue {
  activeContacts: ConnectContact[];
  incomingContact: ConnectContact | null;
  setIncomingContact: (contact: ConnectContact | null) => void;
  /** Ended voice/chat contacts awaiting "Finish". */
  pendingFinishContacts: ConnectContact[];
  setPendingFinishContacts: (contacts: ConnectContact[]) => void;
  /** Clear a pending-finish contact (remote + local session + registry). */
  finishPendingContact: (contact: ConnectContact) => void;
}

const defaultContactsState: AmazonConnectContactsContextValue = {
  activeContacts: [],
  incomingContact: null,
  setIncomingContact: () => {},
  pendingFinishContacts: [],
  setPendingFinishContacts: () => {},
  finishPendingContact: () => {},
};

const AmazonConnectContactsContext =
  createContext<AmazonConnectContactsContextValue>(defaultContactsState);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const sessionHelpers: SessionHelpers = {
  isInMissedState: isContactInMissedState,
  isInAfterCallWork: isContactInAfterChatWorkState,
  hasActiveCallLeg,
};

// ---------------------------------------------------------------------------
// Effects bridge — translates machine commands to React/Redux/callService
// ---------------------------------------------------------------------------

function useContactEffects(
  dispatch: ReturnType<typeof useDispatch>,
  setIncomingContact: (c: ConnectContact | null) => void,
  syncRef: React.RefObject<() => void>
): ContactEffects {
  // -- metadata fetch (stable ref to avoid re-creating effects) --
  const fetchMetadataRef = useRef(
    async (callScopeId: string, isOutbound?: boolean) => {
      try {
        const data = await fetchCallMetadata(callScopeId, isOutbound);
        dispatch(
          setContactInformation({
            contactId: callScopeId,
            callerName: data.callerName,
            callerPhoneNumber: data.callerPhoneNumber,
            authenticated: data.authenticated,
            isDirectConnect: data.isDirectConnect,
            isCallback: data.isCallback,
          })
        );
      } catch {
        /* fetch failed — non-critical */
      }
    }
  );

  // -- stable refs for callbacks that need current values --
  const setIncomingRef = useRef(setIncomingContact);
  setIncomingRef.current = setIncomingContact;

  // Build effects once (empty deps — all deps are stable refs).
  return useRef<ContactEffects>({
    setIncoming(contact: ConnectContact) {
      setIncomingRef.current(contact);
    },

    clearIncoming() {
      setIncomingRef.current(null);
    },

    clearContactInformation() {
      dispatch(setContactInformation(null));
    },

    fetchMetadata(callScopeId: string, isOutbound = false) {
      return fetchMetadataRef.current(callScopeId, isOutbound);
    },

    registerSession(
      callScopeId: string,
      type: 'voice' | 'chat',
      startedAt?: number,
      focus?: boolean
    ) {
      dispatch(
        registerContactSession({
          contactId: callScopeId,
          type,
          startedAt,
          focus,
        })
      );
    },

    initiateStream(callScopeId: string, isOutbound = false) {
      callService.initiateCall(callScopeId, { isOutbound });
    },

    setAfterCallWork(_contact: ConnectContact) {
      // pendingFinishContacts is derived from the manager snapshot via
      // sync(), not individually set here — but sync() only runs after a
      // render, and Connect lifecycle events don't cause renders by
      // themselves.  Trigger the re-sync so the ACW banner appears and the
      // active banner disappears immediately.
      syncRef.current();
    },

    removeFromActive(_contact: ConnectContact) {
      // Active contacts are derived from the manager snapshot, not
      // individually removed.  Trigger the re-sync so ended / missed /
      // destroyed contacts leave the UI immediately instead of lingering
      // until an unrelated render.
      syncRef.current();
    },

    setAgentNotRoutable() {
      connectAdapter.setAgentStateByName(FORCED_RELEASE_STATE, {});
    },

    clearConnectContact(contact: ConnectContact) {
      try {
        contact.clear({ success: () => {}, failure: () => {} });
      } catch {
        /* ok */
      }
    },

    autoAcceptContact(contact: ConnectContact) {
      try {
        contact.accept({ success: () => {}, failure: () => {} });
      } catch {
        /* ok */
      }
    },

    handleChatAccepted(contact: ConnectContact) {
      return connectAdapter.handleChatAccepted(contact);
    },

    clearStream(callScopeId: string) {
      callService.clearContact(callScopeId);
    },
  }).current;
}

// ---------------------------------------------------------------------------
// Snapshot → React state sync
// ---------------------------------------------------------------------------

function useSnapshotSync(
  managerRef: React.RefObject<ContactSessionManager | null>
) {
  const [activeContacts, setActiveContacts] = useState<ConnectContact[]>([]);
  const [incomingContact, setIncomingContact] = useState<ConnectContact | null>(
    null
  );
  const [pendingFinishContacts, setPendingFinishContacts] = useState<
    ConnectContact[]
  >([]);

  // Track previous snapshot to avoid infinite re-render loops.
  const prevRef = useRef<{
    activeIds: string;
    incomingId: string | null;
    pendingFinishIds: string;
  }>({
    activeIds: '',
    incomingId: null,
    pendingFinishIds: '',
  });

  const sync = useCallback(() => {
    const m = managerRef.current;
    if (!m) return;
    const snap = m.getSnapshot();

    const activeIds = snap.active
      .map(r => r.id)
      .sort()
      .join(',');
    const incomingId = snap.incomingId;
    const pendingFinishIds = [...snap.pendingFinishIds].sort().join(',');

    // Only update React state if something actually changed.
    if (
      activeIds !== prevRef.current.activeIds ||
      incomingId !== prevRef.current.incomingId ||
      pendingFinishIds !== prevRef.current.pendingFinishIds
    ) {
      prevRef.current = { activeIds, incomingId, pendingFinishIds };
      setActiveContacts(snap.active.map(r => r.contact));
      setIncomingContact(() => {
        if (!incomingId) return null;
        const rec = m.getRecord(incomingId);
        return rec ? rec.contact : null;
      });
      setPendingFinishContacts(() =>
        snap.pendingFinishIds
          .map(id => m.getRecord(id))
          .filter((r): r is NonNullable<typeof r> => r != null)
          .map(r => r.contact)
      );
    }
  }, [managerRef]);

  return {
    activeContacts,
    incomingContact,
    setIncomingContact,
    pendingFinishContacts,
    setPendingFinishContacts,
    sync,
  };
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

function useAmazonConnectContactsState(): AmazonConnectContactsContextValue {
  const { isReady } = useAmazonConnectReady();
  const { agent } = useAmazonConnectAgent();
  const dispatch = useDispatch();

  const managerRef = useRef<ContactSessionManager | null>(null);
  const detachRef = useRef<(() => void) | null>(null);

  const {
    activeContacts,
    incomingContact,
    setIncomingContact,
    pendingFinishContacts,
    setPendingFinishContacts,
    sync,
  } = useSnapshotSync(managerRef);

  // -- effects bridge --
  // sync is a stable useCallback ([managerRef]); the ref follows the same
  // pattern as setIncomingRef inside useContactEffects.
  const syncRef = useRef(sync);
  syncRef.current = sync;
  const effects = useContactEffects(dispatch, setIncomingContact, syncRef);

  const finishPendingContact = useCallback(
    (contact: ConnectContact) => {
      // Resolve the session key from the registry record (stable, captured at
      // registration) with fallbacks to the live contact object. Using the
      // record avoids a StateError when reading ids off an ended contact.
      let callScopeId: string | null = null;
      let contactId: string | null = null;
      try {
        contactId = contact.getContactId();
      } catch {
        /* contact may be destroyed */
      }
      if (contactId) {
        callScopeId =
          managerRef.current?.getRecord(contactId)?.callScopeId ?? null;
      }
      if (!callScopeId) {
        try {
          callScopeId = connectAdapter.getCallScopeContactId(contact);
        } catch {
          /* fall through */
        }
      }
      if (!callScopeId) {
        callScopeId = contactId;
      }

      try {
        if (!contact.isConnected()) {
          contact.clear({ success: () => {}, failure: () => {} });
        }
      } catch {
        /* already destroyed remotely */
      }
      if (callScopeId) {
        callService.clearContact(callScopeId);
      }
      // Drop the record from the registry so the pending tab disappears even if
      // contact.clear() never emitted onDestroy (e.g. voice already ended).
      if (contactId) {
        managerRef.current?.dismissContact(contactId);
      }
      sync();

      // If nothing is left pending and no live contact remains, return the
      // agent to routable (finishing ACW).
      const snap = managerRef.current?.getSnapshot();
      const hasLiveContact =
        snap?.active.some(rec => {
          try {
            return hasActiveCallLeg(rec.contact);
          } catch {
            return false;
          }
        }) ?? false;
      const hasPending = (snap?.pendingFinishIds.length ?? 0) > 0;
      if (!hasLiveContact && !hasPending) {
        connectAdapter.setAgentState('ROUTABLE', {
          success: () => {},
          failure: () => {},
        });
      }
    },
    [sync]
  );

  // -- initialise / tear down --
  useEffect(() => {
    if (!isReady) {
      detachRef.current?.();
      managerRef.current = null;
      setIncomingContact(null);
      setPendingFinishContacts([]);
      return;
    }

    const manager = new ContactSessionManager(
      connectAdapter,
      effects,
      sessionHelpers
    );
    managerRef.current = manager;
    detachRef.current = manager.attach();

    return () => {
      manager.reset();
      managerRef.current = null;
      detachRef.current = null;
    };
    // Only re-initialise when isReady toggles.  effects is a stable ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // -- seed from agent.getContacts() on agent change --
  useEffect(() => {
    if (!isReady || !agent) return;
    const manager = managerRef.current;
    if (!manager) return;

    try {
      const contacts = agent.getContacts();
      if (contacts?.length) {
        manager.seed(contacts);
        sync();
      }
    } catch {
      /* ignore */
    }
  }, [isReady, agent, sync]);

  // -- sync snapshot → React after every manager event --
  // The manager fires commands synchronously in response to adapter events.
  // We need to re-sync React state after each batch.  A simple approach:
  // after every render triggered by an event, sync.
  useEffect(() => {
    sync();
  });

  // -- sync activeContacts → Redux sessions --
  useEffect(() => {
    for (const contact of activeContacts) {
      try {
        if (!hasActiveCallLeg(contact)) continue;
        const contactId = connectAdapter.getCallScopeContactId(contact);
        dispatch(
          registerContactSession({
            contactId,
            type: connectAdapter.isChatContact(contact) ? 'chat' : 'voice',
          })
        );
      } catch {
        /* contact may have been destroyed */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContacts]);

  return {
    activeContacts,
    incomingContact,
    setIncomingContact,
    pendingFinishContacts,
    setPendingFinishContacts,
    finishPendingContact,
  };
}

// ---------------------------------------------------------------------------
// Provider + consumer
// ---------------------------------------------------------------------------

export function AmazonConnectContactsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const value = useAmazonConnectContactsState();
  return createElement(
    AmazonConnectContactsContext.Provider,
    { value },
    children
  );
}

export function useAmazonConnectContacts(): AmazonConnectContactsContextValue {
  return useContext(AmazonConnectContactsContext);
}
